<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-12">
        <form @submit.prevent="saveSpeaker()" autocomplete="off">
          <!-- Header Card with Actions -->
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h4 class="card-title mb-1">{{ speaker.id ? 'Editar Palestrante' : 'Novo Palestrante' }}</h4>
                  <p class="text-muted mb-0">Preencha as informações do palestrante</p>
                </div>
                <div>
                  <button type="button" @click="cancelar()" class="btn btn-outline-secondary mr-2" :disabled="saving">
                    <i class="bx bx-x mr-1"></i> Cancelar
                  </button>
                  <button type="submit" class="btn btn-primary" :disabled="saving">
                    <i class="bx bx-save mr-1"></i> {{ saving ? 'Salvando...' : 'Salvar' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Profile Section -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">
                <i class="bx bx-user text-primary mr-2"></i>
                Informações do Perfil
              </h5>
              <div class="row">
                <!-- Avatar Section -->
                <div class="col-md-3 col-lg-2 text-center mb-4 mb-md-0">
                  <input
                    ref="photo"
                    type="file"
                    name="photo"
                    accept="image/*"
                    @change="setImage"
                  />
                  <div class="avatar-upload-wrapper" @click="showChooser">
                    <div class="avatar-preview">
                      <img :src="cropImg || (speaker.photo || avatar)" class="rounded-circle">
                      <div class="avatar-overlay">
                        <i class="bx bx-camera"></i>
                      </div>
                    </div>
                    <p class="text-muted mt-2 mb-0 small">Clique para alterar</p>
                  </div>
                </div>

                <!-- Name Field & Company Logo -->
                <div class="col-md-9 col-lg-10">
                  <div class="row">
                    <div class="col-12">
                      <div class="form-group">
                        <label for="name">
                          Nome Completo
                          <span class="text-danger">*</span>
                        </label>
                        <input
                          id="name"
                          v-model="speaker.name"
                          name="name"
                          type="text"
                          class="form-control form-control-lg"
                          placeholder="Nome do palestrante"
                          :class="{
                            'is-invalid': submitted && $v.speaker.name.$error,
                          }"
                        />
                        <div
                          v-if="submitted && !$v.speaker.name.required"
                          class="invalid-feedback"
                        >
                          Nome do Palestrante é obrigatório.
                        </div>
                      </div>
                    </div>
                    <div class="col-12">
                      <div class="form-group">
                        <label>
                          <i class="bx bx-buildings text-primary mr-1"></i>
                          Logo da Empresa
                          <span class="text-muted font-size-12 ml-1">(opcional)</span>
                        </label>
                        <div class="d-flex align-items-center">
                          <input
                            ref="companyLogo"
                            type="file"
                            name="company_logo"
                            accept="image/*"
                            @change="setCompanyLogo"
                          />
                          <div class="company-logo-preview mr-3" @click="showLogoChooser">
                            <img v-if="companyLogoCropImg || speaker.company_logo" :src="companyLogoCropImg || speaker.company_logo" />
                            <div v-else class="company-logo-placeholder">
                              <i class="bx bx-image-add"></i>
                            </div>
                            <div class="company-logo-overlay">
                              <i class="bx bx-camera"></i>
                            </div>
                          </div>
                          <div>
                            <p class="text-muted mb-1 small">Clique para enviar o logo da empresa do palestrante.</p>
                            <p class="text-muted mb-0 small">Formatos: JPG, PNG. Máx: 2MB</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Translatable Content Section -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">
                <i class="bx bx-world text-primary mr-2"></i>
                Conteúdo
                <span v-if="isMultiLanguage" class="badge badge-soft-info ml-2">Multi-idioma</span>
              </h5>

              <!-- Single Language Mode -->
              <div v-if="!isMultiLanguage">
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label for="title">
                        Ocupação / Cargo
                        <span class="text-danger">*</span>
                      </label>
                      <input
                        id="title"
                        v-model="speaker.title"
                        type="text"
                        class="form-control"
                        placeholder="Ex: CEO, Desenvolvedor Senior, etc."
                        :class="{ 'is-invalid': submitted && $v.speaker.title.$error }"
                      />
                      <div
                        v-if="submitted && !$v.speaker.title.required"
                        class="invalid-feedback"
                      >
                        Ocupação do palestrante é obrigatório
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <div class="form-group">
                      <label for="description">Biografia</label>
                      <textarea
                        id="description"
                        v-model="speaker.description"
                        class="form-control"
                        rows="5"
                        placeholder="Escreva uma breve biografia do palestrante..."
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Multi Language Mode -->
              <div v-else>
                <b-tabs v-model="selectedLanguageTab" content-class="mt-3" nav-class="nav-tabs-custom">
                  <b-tab
                    v-for="(lang, index) in activeLanguages"
                    :key="lang.id"
                    :active="index === 0"
                  >
                    <template #title>
                      <span class="d-flex align-items-center">
                        <span v-if="lang.flag" class="mr-1">{{ lang.flag }}</span>
                        {{ lang.name || lang.native_name }}
                        <span v-if="lang.is_default" class="badge badge-soft-primary ml-1">Padrão</span>
                      </span>
                    </template>

                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label>
                            Ocupação / Cargo
                            <span v-if="lang.is_default" class="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            class="form-control"
                            :placeholder="`Ocupação em ${lang.native_name}`"
                            :value="getTranslationValue(lang.id, 'title')"
                            @input="setTranslationValue(lang.id, 'title', $event.target.value)"
                            :class="{ 'is-invalid': submitted && lang.is_default && $v.speaker.title.$error }"
                          />
                          <div
                            v-if="submitted && lang.is_default && !$v.speaker.title.required"
                            class="invalid-feedback"
                          >
                            Ocupação do palestrante é obrigatório
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12">
                        <div class="form-group">
                          <label>Biografia</label>
                          <textarea
                            class="form-control"
                            rows="5"
                            :placeholder="`Biografia em ${lang.native_name}`"
                            :value="getTranslationValue(lang.id, 'description')"
                            @input="setTranslationValue(lang.id, 'description', $event.target.value)"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </b-tab>
                </b-tabs>
              </div>
            </div>
          </div>

          <!-- Social Media Section -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">
                <i class="bx bx-share-alt text-primary mr-2"></i>
                Mídias Sociais
              </h5>
              <div class="row">
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="facebook">
                      <i class="bx bxl-facebook-circle text-primary mr-1"></i>
                      Facebook
                    </label>
                    <input
                      id="facebook"
                      v-model="speaker.facebook"
                      name="facebook"
                      type="text"
                      class="form-control"
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="instagram">
                      <i class="bx bxl-instagram text-danger mr-1"></i>
                      Instagram
                    </label>
                    <input
                      id="instagram"
                      v-model="speaker.instagram"
                      name="instagram"
                      type="text"
                      class="form-control"
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="linkedin">
                      <i class="bx bxl-linkedin-square text-info mr-1"></i>
                      LinkedIn
                    </label>
                    <input
                      id="linkedin"
                      v-model="speaker.linkedin"
                      name="linkedin"
                      type="text"
                      class="form-control"
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="twitter">
                      <i class="bx bxl-twitter text-info mr-1"></i>
                      Twitter / X
                    </label>
                    <input
                      id="twitter"
                      v-model="speaker.twitter"
                      name="twitter"
                      type="text"
                      class="form-control"
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="youtube">
                      <i class="bx bxl-youtube text-danger mr-1"></i>
                      YouTube
                    </label>
                    <input
                      id="youtube"
                      v-model="speaker.youtube"
                      name="youtube"
                      type="text"
                      class="form-control"
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="website">
                      <i class="bx bx-globe text-secondary mr-1"></i>
                      Website / E-mail
                    </label>
                    <input
                      id="website"
                      v-model="speaker.website"
                      name="website"
                      type="text"
                      class="form-control"
                      placeholder="https://... ou email@..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile Save Button -->
          <div class="card d-md-none">
            <div class="card-body">
              <div class="d-flex justify-content-end">
                <button type="button" @click="cancelar()" class="btn btn-outline-secondary mr-2" :disabled="saving">
                  Cancelar
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Salvando...' : 'Salvar' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <b-modal
      id="modal-center"
      ref="modal-photo"
      centered
      title="Alterar Foto"
      title-class="font-18"
      @hidden="cancel('photo', 1)"
      @ok="cropImage"
    >
      <div class="img-cropper border-dashed" v-show="imgSrc">
        <vue-cropper
          ref="cropper"
          :src="imgSrc"
          preview=".preview"
        />
      </div>
    </b-modal>

  </Layout>
</template>

<script>
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import translatableMixin from '@/mixins/translatableMixin'

export default {
  components: {
    VueCropper,
    Layout,
    PageHeader
  },
  mixins: [translatableMixin],
  name: 'Palestrante',
  page: {
    title: 'Palestrante',
    meta: [{
      name: 'description',
      content: appConfig.description
    }]
  },
  data () {
    return {
      speaker: {
        id: '',
        name: '',
        title: '',
        description: '',
        photo: '',
        company_logo: '',
        facebook: '',
        instagram: '',
        linkedin: '',
        twitter: '',
        youtube: '',
        website: ''
      },
      loading: true,
      avatar: require('@/assets/images/users/user-default.png'),
      imgSrc: '',
      cropImg: '',
      companyLogoCropImg: '',
      submitted: false,
      saving: false,
      selectedLanguageTab: 0,
      title: 'Palestrante',
      items: [{
        text: 'Palestrantes',
        href: '/speakers'
      },
      {
        text: 'Novo Palestrante',
        active: true
      }
      ]
    }
  },
  validations: {
    speaker: {
      name: { required },
      title: { required }
    }
  },
  async mounted () {
    await this.loadTranslationSettings()
    this.loadItem()
  },
  computed: {
    ...mapState('speaker', { data: 'speaker' })
  },
  methods: {
    ...mapActions('speaker', {
      fetch: 'GetSpeaker',
      postData: 'SaveSpeaker'
    }),
    cancelar () {
      this.$router.push({ name: 'speakers' })
    },
    async loadItem () {
      if (this.$route.params.id !== undefined) {
        let loader = this.$loading.show()
        this.items[1].text = 'Editar Palestrante'
        await this.fetch(this.$route.params.id)
        this.speaker = this.data
        // Load translations for this speaker
        if (this.isMultiLanguage) {
          await this.loadTranslations('participant', this.$route.params.id)
        }
        loader.hide()
        return
      }
      // Initialize translations for new speaker
      if (this.isMultiLanguage) {
        this.initializeTranslations(['title', 'description'])
      }
    },
    cropImage () {
      // get image data for post processing, e.g. upload or setting image src
      this.cropImg = this.$refs.cropper.getCroppedCanvas().toDataURL()
      this.$refs.cropper.getCroppedCanvas().toBlob((blob) => {
        this.speaker.binaryphoto = blob
      })
    },
    cancel (ref) {
      this.$refs[`${ref}`].value = null
      this.imgSrc = ''
    },
    setImage (e) {
      const file = e.target.files[0]
      if (file.type.indexOf('image/') === -1) {
        errorMessage('Alerta: Por favor selecione uma imagem.')
        return
      }
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        errorMessage('Alerta: Tamanho máximo 2MB!')
        return
      }
      if (typeof FileReader === 'function') {
        const reader = new FileReader()
        reader.onload = (event) => {
          this.imgSrc = event.target.result
          // rebuild cropperjs with the updated source
          this.$refs.cropper.replace(event.target.result)
        }
        reader.readAsDataURL(file)
        this.$refs['modal-photo'].show()
      } else {
        errorMessage('ERROR: Browser não suporta FileReader')
      }
    },
    showChooser () {
      this.$refs.photo.click()
    },
    showLogoChooser () {
      this.$refs.companyLogo.click()
    },
    setCompanyLogo (e) {
      const file = e.target.files[0]
      if (file.type.indexOf('image/') === -1) {
        errorMessage('Alerta: Por favor selecione uma imagem.')
        return
      }
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        errorMessage('Alerta: Tamanho máximo 2MB!')
        return
      }
      this.speaker.binaryCompanyLogo = file
      const reader = new FileReader()
      reader.onload = (event) => {
        this.companyLogoCropImg = event.target.result
      }
      reader.readAsDataURL(file)
    },
    getTranslationValue (languageId, field) {
      // For default language, also sync with main model
      if (languageId === this.defaultLanguageId) {
        if (this.translationData[languageId] && this.translationData[languageId][field]) {
          return this.translationData[languageId][field]
        }
        return this.speaker[field] || ''
      }
      if (this.translationData[languageId] && this.translationData[languageId][field]) {
        return this.translationData[languageId][field]
      }
      return ''
    },
    setTranslationValue (languageId, field, value) {
      // Ensure the language object exists
      if (!this.translationData[languageId]) {
        this.$set(this.translationData, languageId, {})
      }
      this.$set(this.translationData[languageId], field, value)

      // For default language, also update the main model
      if (languageId === this.defaultLanguageId) {
        this.speaker[field] = value
      }
    },
    async saveSpeaker () {
      // Prevent double submission
      if (this.saving) {
        return
      }

      this.submitted = true
      window.scroll(0, 0)
      if (!this.speaker.photo && !this.speaker.binaryphoto) {
        errorMessage('Alerta: Foto do palestrante é obrigatória!')
        return
      }

      this.$v.speaker.$touch()
      if (this.$v.speaker.$invalid) {
        return
      }

      this.saving = true
      let loader = this.$loading.show()
      const formData = new FormData()
      formData.append('id', this.speaker.id)
      formData.append('name', this.speaker.name)
      formData.append('title', this.speaker.title)
      formData.append('description', this.speaker.description)
      formData.append('facebook', this.speaker.facebook)
      formData.append('instagram', this.speaker.instagram)
      formData.append('linkedin', this.speaker.linkedin)
      formData.append('twitter', this.speaker.twitter)
      formData.append('youtube', this.speaker.youtube)
      formData.append('website', this.speaker.website)
      if (this.speaker.binaryphoto) {
        formData.append('photo', this.speaker.binaryphoto, 'avatar.jpg')
      }
      if (this.speaker.binaryCompanyLogo) {
        formData.append('company_logo', this.speaker.binaryCompanyLogo, 'company-logo.jpg')
      }
      try {
        const res = await this.postData(formData)
        // Save translations after saving the speaker
        let speakerId = this.speaker.id
        if (res && res.data && res.data.data && res.data.data.id) {
          speakerId = res.data.data.id
        } else if (res && res.data && res.data.id) {
          speakerId = res.data.id
        }
        if (this.isMultiLanguage && speakerId) {
          await this.saveTranslations('participant', speakerId)
        }
        let message = 'Sucesso: Dados atualizados com sucesso.'
        let delay = 2500
        loader.hide()
        successMessage(message, delay)
        this.cancelar()
      } catch (err) {
        console.error('Error saving speaker:', err)
        errorMessage('Alerta: ' + err.message)
        loader.hide()
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
input[type="file"] {
  display: none;
}

/* Avatar Upload Styles */
.avatar-upload-wrapper {
  cursor: pointer;
}

.avatar-preview {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #e9ecef;
  transition: all 0.3s ease;
}

.avatar-preview:hover {
  border-color: #556ee6;
}

.avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.avatar-preview:hover .avatar-overlay {
  opacity: 1;
}

.avatar-overlay i {
  font-size: 2rem;
  color: #fff;
}

/* Card Styles */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: none;
  margin-bottom: 1.5rem;
}

.card-title {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

.card-title i {
  font-size: 1.25rem;
}

/* Badge Styles */
.badge-soft-info {
  background-color: rgba(85, 110, 230, 0.1);
  color: #556ee6;
}

.badge-soft-primary {
  background-color: rgba(85, 110, 230, 0.18);
  color: #556ee6;
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
}

/* Form Styles */
.form-control-lg {
  font-size: 1rem;
}

/* Cropper Modal */
.border-dashed {
  border: 2px dashed #ced4da !important;
  padding: 4px;
}

.img-cropper {
  max-height: 400px;
}

/* Company Logo Upload */
.company-logo-preview {
  width: 80px;
  height: 80px;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 2px dashed #e9ecef;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.company-logo-preview:hover {
  border-color: #556ee6;
}

.company-logo-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
}

.company-logo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 1.5rem;
}

.company-logo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 0.5rem;
}

.company-logo-preview:hover .company-logo-overlay {
  opacity: 1;
}

.company-logo-overlay i {
  font-size: 1.25rem;
  color: #fff;
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .avatar-preview {
    width: 100px;
    height: 100px;
  }
}
</style>
